import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import useFormState from "./useFormState";
import { toCreateRequestDto } from "./mappers";
import { emptyAddress, isAddressComplete } from "./address";
import { requiresProofOfReturn, toAttachmentCategoryCode } from "./options";
import { isFutureIsoDate, isValidEmail, isValidPhone } from "./validation";

const INITIAL_VALUES = {
  submitterType: "customer",
  retailer: "",
  store: "",
  retailerPhone: "",
  retailerEmail: "",
  retailerAddress: emptyAddress(),
  deliverySameAsAbove: true,
  deliveryAddress: emptyAddress(),
  customerFirstName: "",
  customerLastName: "",
  customerEmail: "",
  customerPhone: "",
  customerAddress: emptyAddress(),
  // Only asked when a retailer is lodging; a customer's primary email is always their own.
  primaryEmail: null,
  claimType: "DOA",
  claimTypeOther: "",
  brandId: "",
  deviceId: "",
  serialNumber: "",
  popDate: "",
  porDate: "",
  solvupCase: "",
  storeReference: "",
  faults: [],
  faultDescription: "",
  // Everything below is only asked of retailers.
  replicatedInStore: null,
  replicationSteps: "",
  waterAndDebrisRemoved: null,
  cosmeticCondition: null,
  cosmeticConditionOther: "",
  missingAccessories: "",
  agreed: true,
};

function makeUploadId() {
  return (
    "upload-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2)
  );
}

/*
All request-form state and its conditional rules:
- customers are asked for their own address and fewer retail/product fields;
  their reason for submission is hidden and always DOA
- retailers also choose the primary email
- both answer the pre-qualification questions (replication, cosmetic
  condition, water/debris, accessories)
- "Other" claim type reveals a free-text explanation
- warranty service claims do not collect proof of return
- the submit button stays disabled until the terms are accepted

On success the staff member is taken to the new case with a toast carrying
its reference number.
*/
export default function useRequestForm(protectedFetch) {
  const history = useHistory();
  const { showToast } = useToast();
  const form = useFormState(INITIAL_VALUES);
  const [uploads, setUploads] = useState([]);
  const [step, setStep] = useState(1);
  const [page1Attempted, setPage1Attempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const addUploads = useCallback(function (files, category = "Other") {
    const drafts = files.map(function (file) {
      return { id: makeUploadId(), name: file.name, category, file };
    });
    setUploads(function (previous) {
      return [...previous, ...drafts];
    });
  }, []);

  const removeUpload = useCallback(function (id) {
    setUploads(function (previous) {
      return previous.filter(function (upload) {
        return upload.id !== id;
      });
    });
  }, []);

  const setUploadCategory = useCallback(function (id, category) {
    setUploads(function (previous) {
      return previous.map(function (upload) {
        return upload.id === id ? { ...upload, category } : upload;
      });
    });
  }, []);

  const { values, setValues } = form;
  const isCustomer = values.submitterType === "customer";

  const setSubmitterType = useCallback(
    function (submitterType) {
      if (submitterType === "customer") {
        setValues({
          ...values,
          submitterType,
          // Customers don't see the reason field; their claim is always a DOA.
          claimType: "DOA",
          claimTypeOther: "",
          porDate: "",
          solvupCase: "",
          storeReference: "",
          primaryEmail: null,
        });
        // Customers can't label a file as POR, so relabel any left over from retailer mode.
        setUploads(function (previous) {
          return previous.map(function (upload) {
            return upload.category === "Proof of Return (POR)"
              ? { ...upload, category: "Other" }
              : upload;
          });
        });
      } else {
        setValues({
          ...values,
          submitterType,
          // Undo the claim type forced for customers.
          claimType:
            values.submitterType === "customer" ? INITIAL_VALUES.claimType : values.claimType,
        });
      }
    },
    [values, setValues],
  );

  const porRequired = !isCustomer && requiresProofOfReturn(values.claimType);

  // Retailer contact details are only asked (and so only validated) when a retailer is lodging.
  const retailerContactValid =
    isCustomer ||
    ((values.retailerEmail === "" || isValidEmail(values.retailerEmail)) &&
      (values.retailerPhone === "" || isValidPhone(values.retailerPhone)));

  // Retailers choose whose email is primary; customers are never asked.
  const primaryEmailChosen = isCustomer || values.primaryEmail !== null;

  const customerAddressComplete = !isCustomer || isAddressComplete(values.customerAddress);
  const retailerAddressComplete = isCustomer || isAddressComplete(values.retailerAddress);
  // A separate delivery address (box unticked) must be complete, in either mode.
  const deliveryAddressComplete =
    values.deliverySameAsAbove || isAddressComplete(values.deliveryAddress);

  const datesValid =
    !isFutureIsoDate(values.popDate) &&
    (!porRequired ||
      (!isFutureIsoDate(values.porDate) &&
        (values.popDate === "" ||
          values.porDate === "" ||
          values.porDate >= values.popDate)));

  const canProceedToPage2 =
    values.submitterType !== null &&
    values.retailer !== "" &&
    values.customerFirstName.trim() !== "" &&
    values.customerLastName.trim() !== "" &&
    isValidEmail(values.customerEmail) &&
    isValidPhone(values.customerPhone) &&
    retailerContactValid &&
    primaryEmailChosen &&
    customerAddressComplete &&
    retailerAddressComplete &&
    deliveryAddressComplete &&
    values.brandId !== "" &&
    values.deviceId !== "" &&
    (isCustomer || values.claimType !== "Other" || values.claimTypeOther.trim() !== "") &&
    datesValid;

  const canSubmit =
    values.agreed &&
    isValidEmail(values.customerEmail) &&
    isValidPhone(values.customerPhone) &&
    retailerContactValid &&
    primaryEmailChosen &&
    datesValid &&
    !isSubmitting;

  const goToNextPage = useCallback(
    function () {
      setPage1Attempted(true);
      if (canProceedToPage2) setStep(2);
    },
    [canProceedToPage2],
  );

  const goToPreviousPage = useCallback(function () {
    setStep(1);
  }, []);

  const submit = useCallback(
    function () {
      if (!canSubmit) return;

      setIsSubmitting(true);
      setSubmitError(null);

      const uploadTokens = uploads.map(function (upload) {
        const body = new FormData();
        body.append("category", toAttachmentCategoryCode(upload.category));
        body.append("file", upload.file);
        return protectedFetch(
          apiEndpoints.portal.uploadAttachment(),
          "POST",
          body,
          errorFunctions.checkResponseThenJson,
          true,
        ).then(function (res) {
          return res.upload_token;
        });
      });

      Promise.all(uploadTokens)
        .then(function (attachmentTokens) {
          return protectedFetch(
            apiEndpoints.portal.createRequest(),
            "POST",
            toCreateRequestDto(values, attachmentTokens),
            errorFunctions.checkResponseThenJson,
          );
        })
        .then(function (res) {
          setIsSubmitting(false);
          showToast(
            `New Request ${res.ticket_number} was submitted successfully.`,
            "success",
          );
          history.push("/ticket-new/" + encodeURIComponent(res.id));
        })
        .catch(function (err) {
          setIsSubmitting(false);
          setSubmitError((err && err.message) || "Failed to submit request.");
        });
    },
    [canSubmit, uploads, values, protectedFetch, showToast, history],
  );

  return {
    form,
    uploads,
    addUploads,
    removeUpload,
    setUploadCategory,
    isCustomer,
    setSubmitterType,
    claimIsOther: !isCustomer && values.claimType === "Other",
    porRequired,
    canSubmit,
    isSubmitting,
    submitError,
    submit,
    step,
    page1Attempted,
    canProceedToPage2,
    goToNextPage,
    goToPreviousPage,
  };
}
